/**
 * CancelOrderDrawer — slide-out panel for admin-initiated order cancellation.
 *
 * Mirrors ReprintDrawer: outer component owns the mutation + query
 * invalidation + close guard; inner component owns form state so it resets
 * when the drawer unmounts on close.
 *
 * On success the panel keeps an in-panel confirmation rather than closing, so
 * the customer is told the outcome depends on the printer's answer.
 *
 * The trimmed reason is sent with the V2 mutation and persisted on the
 * resulting OrderEvent; MAX_REASON_LENGTH mirrors the backend @Size cap so
 * the input caps at the same length the server will accept.
 */
import { Alert, Button, tokens } from "@blurb/codex-react";
import { WarningIcon } from "@blurb/codex-react/icons";
import { createStyles } from "@mantine/core";
import { GraphQL, isGraphQLError } from "@rpi/openapi-api";
import { useToasts } from "@rpi/openapi-core";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { RpaDrawer, RpaDrawerOverlay } from "../../rpi-core/drawer/RpaDrawer";
import { Body, Label } from "../../components/Typography";
import { RpaTextInput } from "../../components/inputFields/RpaTextInput";

export const CANCEL_ORDER_TOAST = {
  failure: "Failed to process cancellation request",
} as const;

// Sandbox orders are still sent to UOS, so the printer can still refuse — but they
// carry no payment, so the refund half of the warning would be untrue there.
const CANCEL_ORDER_EXPECTATION = {
  live: "If printing hasn't started, you'll be refunded in full. Once it has, we can only refund shipping — and if your order is already shipping, we may not be able to cancel it.",
  sandbox:
    "There are no payments for test orders, so cancelling won't produce a refund. If this order is already shipping, we may not be able to cancel it.",
} as const;

const CANCEL_ORDER_SUBMITTED = {
  message: "Cancellation request sent to the printer",
  description: "What happens next depends on the printer's answer. We'll update this order as soon as they respond.",
} as const;

const MAX_REASON_LENGTH = 500;

const useStyles = createStyles({
  notice: {
    display: "flex",
    alignItems: "flex-start",
    gap: tokens.spacing[2],
  },
  noticeIcon: {
    flex: "0 0 auto",
    color: tokens.color.semantic.icon.warning,
  },
  cancelLink: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    alignSelf: "center",
  },
  cancelText: {
    color: tokens.color.semantic.text.link,
    borderBottom: "1px solid currentColor",
    paddingBottom: 2,
  },
});

interface CancelOrderDrawerProps {
  opened: boolean;
  onClose: () => void;
  order: GraphQL.OrderMinimalQuery["orderByIdentifier"];
  isSandbox: boolean;
}

export function CancelOrderDrawer({ opened, onClose, order, isSandbox }: CancelOrderDrawerProps) {
  const { showErrorToast } = useToasts();
  const queryClient = useQueryClient();
  const [submitted, setSubmitted] = React.useState(false);

  // The confirmation belongs to the order it was submitted for, so it clears
  // when the drawer shows a different order. On close it instead survives
  // until reopen, so the reason form doesn't reappear behind the closing
  // animation.
  const orderKey = `${order?.customer?.publicId ?? ""}:${order?.customerOrderId ?? ""}`;
  const [wasOpened, setWasOpened] = React.useState(opened);
  const [wasOrderKey, setWasOrderKey] = React.useState(orderKey);
  if (opened !== wasOpened || orderKey !== wasOrderKey) {
    setWasOpened(opened);
    setWasOrderKey(orderKey);
    if (opened || orderKey !== wasOrderKey) setSubmitted(false);
  }

  const { mutate: submitCancel, isLoading } = GraphQL.useAdminCancelOrderV2Mutation({
    onSuccess: async () => {
      setSubmitted(true);
      await queryClient.invalidateQueries({ queryKey: ["Order"] });
    },
    onError: (error) => {
      showErrorToast(isGraphQLError(error) ? error.message : CANCEL_ORDER_TOAST.failure);
    },
  });

  // Block close during in-flight mutation so the user doesn't orphan a cancel request.
  const handleClose = () => {
    if (!isLoading) onClose();
  };

  const handleSubmit = (reason: string) => {
    if (!order?.customer?.publicId || !order?.customerOrderId) return;
    submitCancel({
      input: {
        orderIdentifier: {
          customerPublicId: order.customer.publicId,
          customerOrderId: order.customerOrderId,
        },
        reason,
      },
    });
  };

  return (
    <RpaDrawer
      title={<span data-testid="cancel-order-drawer-title">{`Cancel Order: ${order?.customerOrderId ?? ""}`}</span>}
      opened={opened}
      onClose={handleClose}
    >
      {submitted ? (
        <CancelOrderSubmitted onDone={handleClose} />
      ) : (
        <CancelOrderDrawerContents
          key={orderKey}
          isLoading={isLoading}
          isSandbox={isSandbox}
          onClose={handleClose}
          onSubmit={handleSubmit}
        />
      )}
    </RpaDrawer>
  );
}

/**
 * Post-submit confirmation. Rendered in flow rather than through RpaDrawer's
 * `overlay` prop, which is absolutely positioned over the header and would
 * cover the drawer's own close button.
 */
function CancelOrderSubmitted({ onDone }: { onDone: () => void }) {
  return (
    <>
      <RpaDrawerOverlay
        data-testid="cancel-order-submitted"
        overlay={false}
        type="success"
        message={CANCEL_ORDER_SUBMITTED.message}
        description={CANCEL_ORDER_SUBMITTED.description}
      />
      <RpaDrawer.Footer>
        <RpaDrawer.FooterActions align="stretch" gap={3}>
          <Button data-testid="cancel-order-done" variant="filled" size="large" onClick={onDone}>
            Done
          </Button>
        </RpaDrawer.FooterActions>
      </RpaDrawer.Footer>
    </>
  );
}

interface CancelOrderDrawerContentsProps {
  isLoading: boolean;
  isSandbox: boolean;
  onSubmit: (reason: string) => void;
  onClose: () => void;
}

function CancelOrderDrawerContents({ isLoading, isSandbox, onSubmit, onClose }: CancelOrderDrawerContentsProps) {
  const { classes } = useStyles();
  const [reason, setReason] = React.useState("");
  const [attemptedSubmit, setAttemptedSubmit] = React.useState(false);

  const trimmedReason = reason.trim();
  const isReasonBlank = trimmedReason.length === 0;

  const handleSubmit = () => {
    setAttemptedSubmit(true);
    if (isReasonBlank) return;
    onSubmit(trimmedReason);
  };

  return (
    <>
      <RpaDrawer.Body>
        <Alert type="warning" size="large">
          <div className={classes.notice} data-testid="cancel-order-expectation-notice">
            <WarningIcon size="xl" className={classes.noticeIcon} />
            <Body size="sm">{isSandbox ? CANCEL_ORDER_EXPECTATION.sandbox : CANCEL_ORDER_EXPECTATION.live}</Body>
          </div>
        </Alert>
        <RpaTextInput
          data-testid="cancel-order-reason-input"
          label={
            <Label size="base" weight="semibold">
              Reason
            </Label>
          }
          placeholder="Enter"
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          maxLength={MAX_REASON_LENGTH}
          error={attemptedSubmit && isReasonBlank ? "Please provide a reason" : undefined}
        />
      </RpaDrawer.Body>
      <RpaDrawer.Footer>
        <RpaDrawer.FooterActions align="stretch" gap={3}>
          <Button
            data-testid="cancel-order-submit"
            variant="filled"
            intent="danger"
            size="large"
            loading={isLoading}
            onClick={handleSubmit}
          >
            Cancel Order
          </Button>
          <button type="button" className={classes.cancelLink} onClick={onClose} disabled={isLoading}>
            <Body size="base" weight="semibold" className={classes.cancelText}>
              Never Mind
            </Body>
          </button>
        </RpaDrawer.FooterActions>
      </RpaDrawer.Footer>
    </>
  );
}
